/** World/local adapters for the shared finite-solid queries. No render-mesh raycasts. */
import * as THREE from 'three';
import { cliffHit, cliffMotionScratch, moveCliffCapsule } from '@hyperlight/shared';
import type { PlanetHost } from './planetHost';

export class TerrainContacts {
  private a = [0, 0, 0];
  private b = [0, 0, 0];
  private up = [0, 1, 0];
  private velocity = [0, 0, 0];
  private result = [0, 0, 0];
  private vec = new THREE.Vector3();
  private motion = cliffMotionScratch();
  readonly hit = cliffHit();
  readonly normal = new THREE.Vector3();
  private local(planet: PlanetHost, p: THREE.Vector3, out: number[]): void {
    this.vec.copy(p).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
    out[0] = this.vec.x; out[1] = this.vec.y; out[2] = this.vec.z;
  }
  move(planet: PlanetHost, from: THREE.Vector3, to: THREE.Vector3, velocity: THREE.Vector3,
    height = 1.8, radius = .45): boolean {
    const field = planet.terrainSolids;
    if (!field?.solids.length) return false;
    this.local(planet, from, this.a); this.local(planet, to, this.b);
    const r = Math.hypot(...this.a) || 1;
    for (let i = 0; i < 3; i++) this.up[i] = this.a[i] / r;
    this.vec.copy(velocity).applyQuaternion(planet.pose.invQuat);
    this.velocity[0] = this.vec.x; this.velocity[1] = this.vec.y; this.velocity[2] = this.vec.z;
    const touched = moveCliffCapsule(field, this.a, this.b, this.up, height, radius,
      this.velocity, this.result, this.motion);
    to.set(this.result[0], this.result[1], this.result[2]).applyQuaternion(planet.pose.quat).add(planet.pose.center);
    velocity.set(this.velocity[0], this.velocity[1], this.velocity[2]).applyQuaternion(planet.pose.quat);
    return touched;
  }
  /** Position-aware support. A limited rise catches steps, never a roof over the head. */
  support(planet: PlanetHost, feet: THREE.Vector3, rise = .06, maxDrop = 2): number | null {
    const field = planet.terrainSolids;
    if (!field?.solids.length) return null;
    this.local(planet, feet, this.a);
    const r = Math.hypot(...this.a) || 1;
    for (let i = 0; i < 3; i++) this.a[i] *= (r + rise) / r;
    const hit = field.supportBelow(this.a, maxDrop + rise, this.hit);
    if (!hit) return null;
    this.normal.set(...hit.normal).applyQuaternion(planet.pose.quat).normalize();
    return Math.hypot(...hit.point);
  }
  occupied(planet: PlanetHost, feet: THREE.Vector3, height: number, radius = .45): boolean {
    const field = planet.terrainSolids;
    if (!field?.solids.length) return false;
    this.local(planet, feet, this.a);
    const r = Math.hypot(...this.a) || 1;
    for (let i = 0; i < 3; i++) { this.b[i] = this.a[i] * (r + height - radius) / r; this.a[i] *= (r + radius) / r; }
    return !!field.capsuleContact(this.a, this.b, radius, this.hit) && this.hit.penetration > .005;
  }
  /** Earliest physical hit; the returned normal is in world space. */
  sweep(planet: PlanetHost, from: THREE.Vector3, to: THREE.Vector3, radius = 0): number | null {
    const field = planet.terrainSolids;
    if (!field?.solids.length) return null;
    this.local(planet, from, this.a); this.local(planet, to, this.b);
    const hit = radius > 0 ? field.sphereSweep(this.a, this.b, radius, this.hit) : field.segmentHit(this.a, this.b, this.hit);
    if (!hit) return null;
    this.normal.set(...hit.normal).applyQuaternion(planet.pose.quat).normalize();
    return hit.t;
  }
}
